/* ============================================================================
   ACEPLORE · module-contact.js — <lumina-contact> progressive contact form.
   The form works fully without JS (standard Shopify {% form 'contact' %} POST).
   This enhances it with: inline required/email validation before submit, a
   busy/loading state on the button, and focus management for the success and
   server-error banners (announced to assistive tech). All listeners are cleaned
   up on disconnect. Respects prefers-reduced-motion (no extra motion here).
   ========================================================================== */
(function () {
  'use strict';

  var Lumina = (window.Lumina = window.Lumina || {});
  var Base = Lumina.Element || window.HTMLElement;

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  class LuminaContact extends Base {
    connectedCallback() {
      this.form = this.querySelector('.lm-contact__form');

      // On a successful or errored POST Shopify re-renders the section; move
      // focus to the relevant banner so the outcome is announced.
      var success = this.querySelector('[data-contact-success]');
      var errors = this.querySelector('[data-contact-errors]');
      var banner = success || errors;
      if (banner) {
        // Defer so layout/reveal has settled before we scroll/focus.
        this._focusTimer = window.setTimeout(function () {
          try { banner.focus({ preventScroll: false }); } catch (e) { banner.focus(); }
        }, 60);
      }

      if (!this.form) return;

      this.submitBtn = this.querySelector('[data-contact-submit]');
      this.fields = Array.prototype.slice.call(
        this.form.querySelectorAll('.lm-input, .lm-textarea')
      );

      this._onSubmit = this.handleSubmit.bind(this);
      this.listen(this.form, 'submit', this._onSubmit);

      // Clear a field's invalid state as soon as the user corrects it.
      this._onInput = this.handleInput.bind(this);
      this.fields.forEach(function (field) {
        this.listen(field, 'input', this._onInput);
      }, this);
    }

    disconnectedCallback() {
      if (this._focusTimer) { window.clearTimeout(this._focusTimer); this._focusTimer = null; }
      if (super.disconnectedCallback) super.disconnectedCallback();
    }

    handleInput(event) {
      var field = event.target;
      if (field.classList.contains('lm-input--invalid')) {
        this.clearError(field);
      }
    }

    handleSubmit(event) {
      // Validate; if anything is invalid, block the native submit and focus the
      // first offending field. Otherwise let the browser POST normally.
      var firstInvalid = null;

      this.fields.forEach(function (field) {
        this.clearError(field);
        var value = (field.value || '').trim();
        var invalid = false;

        if (field.hasAttribute('required') && value === '') {
          invalid = true;
        } else if (field.type === 'email' && value !== '' && !EMAIL_RE.test(value)) {
          invalid = true;
        }

        if (invalid) {
          this.markError(field);
          if (!firstInvalid) firstInvalid = field;
        }
      }, this);

      if (firstInvalid) {
        event.preventDefault();
        try { firstInvalid.focus({ preventScroll: false }); } catch (e) { firstInvalid.focus(); }
        return;
      }

      // Valid — show a busy state. The browser then performs the real POST.
      this.setBusy(true);
    }

    markError(field) {
      field.classList.add('lm-input--invalid');
      field.setAttribute('aria-invalid', 'true');
    }

    clearError(field) {
      field.classList.remove('lm-input--invalid');
      field.removeAttribute('aria-invalid');
    }

    setBusy(busy) {
      if (!this.submitBtn) return;
      if (busy) {
        this.submitBtn.classList.add('is-loading');
        this.submitBtn.setAttribute('aria-disabled', 'true');
        this.submitBtn.setAttribute('aria-busy', 'true');
      } else {
        this.submitBtn.classList.remove('is-loading');
        this.submitBtn.removeAttribute('aria-disabled');
        this.submitBtn.removeAttribute('aria-busy');
      }
    }
  }

  if ('customElements' in window && !customElements.get('lumina-contact')) {
    customElements.define('lumina-contact', LuminaContact);
  }
})();
